import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import FormatService from '../../services/format-service';
import FormService from '../../services/form-service';
import useStylesPasswordStrengthBar from './password-strength-bar.styles';
import {
  REGEXP_TEMPLATE_HAS_NUMBERS, REGEXP_TEMPLATE_HAS_LOWERCASE, REGEXP_TEMPLATE_HAS_UPPERCASE,
  REGEXP_TEMPLATE_HAS_SPECIAL_CHARACTERS,
} from '../../constants/system/regexp-templates';

const PasswordStrengthBar: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const VALIDATION_FIELD_MIN_LENGTH: string = 'minLength';
  const VALIDATION_FIELD_SPECIAL: string = 'special';
  const VALIDATION_FIELD_UPPERCASE: string = 'uppercase';
  const VALIDATION_FIELD_LOWERCASE: string = 'lowercase';
  const VALIDATION_FIELD_NORMAL_LENGTH: string = 'normalLength';
  const VALIDATION_FIELD_NUMBER: string = 'number';
  const VARIANT_DETERMINATE: LinearProgressVariant = 'determinate';
  const PASSWORD_COMPLEXITY_NORMAL: number = 76;
  const PASSWORD_COMPLEXITY_STRONG: number = 88;
  const PASSWORD_COMPLEXITY_DEFAULT: number = 0;
  const PASSWORD_COMPLEXITY_INCREMENT_BY_LENGTH: number = 40;
  const PASSWORD_COMPLEXITY_INCREMENT_BY_RULE: number = 12;

  const {
    passwordValue,
    className,
  } = props;

  const { classes } = useStylesPasswordStrengthBar();

  const INITIAL_VALIDATION_RULES: Record<string, boolean> = useMemo((): Record<string, boolean> => ({
    [VALIDATION_FIELD_SPECIAL]: false,
    [VALIDATION_FIELD_UPPERCASE]: false,
    [VALIDATION_FIELD_LOWERCASE]: false,
    [VALIDATION_FIELD_MIN_LENGTH]: false,
    [VALIDATION_FIELD_NORMAL_LENGTH]: false,
    [VALIDATION_FIELD_NUMBER]: false,
  }), []);

  const [passwordComplexity, setPasswordComplexity]: [number, Function] = useState(PASSWORD_COMPLEXITY_DEFAULT);
  const [validationRules, setValidationRules]: [Record<string, boolean>, Function] = useState(INITIAL_VALIDATION_RULES);

  const isProgressError: boolean = passwordComplexity < PASSWORD_COMPLEXITY_NORMAL;
  const isProgressNormal: boolean = passwordComplexity >= PASSWORD_COMPLEXITY_NORMAL
    && passwordComplexity < PASSWORD_COMPLEXITY_STRONG;
  const isProgressStrong: boolean = passwordComplexity >= PASSWORD_COMPLEXITY_STRONG;

  const progressBarBackgroundClassName: string | undefined = useMemo((): string | undefined => {
    if (isProgressError) {
      return classes.progressBarErrorBackground;
    }

    if (isProgressNormal) {
      return classes.progressBarNormalBackground;
    }

    if (isProgressStrong) {
      return classes.progressBarStrongBackground;
    }

    return undefined;
  }, [isProgressError, isProgressNormal, isProgressStrong]);

  const progressBarClassName: string | undefined = useMemo((): string | undefined => {
    if (isProgressError) {
      return classes.progressBarError;
    }

    if (isProgressNormal) {
      return classes.progressBarNormal;
    }

    if (isProgressStrong) {
      return classes.progressBarStrong;
    }

    return undefined;
  }, [isProgressError, isProgressNormal, isProgressStrong]);

  const calculatePasswordComplexity: Function = useCallback((validationRulesList: Record<string, boolean>): number => {
    let passwordComplexityValue: number = PASSWORD_COMPLEXITY_DEFAULT;
    Object.keys(validationRulesList).forEach((validationRule: string): void => {
      if (validationRule === VALIDATION_FIELD_MIN_LENGTH && validationRulesList[validationRule]) {
        passwordComplexityValue += PASSWORD_COMPLEXITY_INCREMENT_BY_LENGTH;
      } else if (validationRulesList[validationRule]) {
        passwordComplexityValue += PASSWORD_COMPLEXITY_INCREMENT_BY_RULE;
      }
    });

    return passwordComplexityValue;
  }, []);

  const checkValidationRules: Function = useCallback((password: string): void => {
    setValidationRules({
      [VALIDATION_FIELD_LOWERCASE]: REGEXP_TEMPLATE_HAS_LOWERCASE.test(password),
      [VALIDATION_FIELD_UPPERCASE]: REGEXP_TEMPLATE_HAS_UPPERCASE.test(password),
      [VALIDATION_FIELD_SPECIAL]: REGEXP_TEMPLATE_HAS_SPECIAL_CHARACTERS.test(password),
      [VALIDATION_FIELD_NORMAL_LENGTH]: password.length >= FormService.PASSWORD_FIELD_LENGTH_NORMAL,
      [VALIDATION_FIELD_MIN_LENGTH]: password.length >= FormService.PASSWORD_FIELD_LENGTH_MIN,
      [VALIDATION_FIELD_NUMBER]: REGEXP_TEMPLATE_HAS_NUMBERS.test(password),
    });
  }, []);

  useEffect((): void => {
    checkValidationRules(passwordValue);
  }, [passwordValue]);

  useEffect((): void => {
    setPasswordComplexity(calculatePasswordComplexity(validationRules));
  }, [validationRules]);

  return (
    <div className={FormatService.formatClassNames(classes.passwordStrengthBar, className)}>
      <LinearProgress
        value={passwordComplexity}
        variant={VARIANT_DETERMINATE}
        classes={{
          barColorPrimary: progressBarClassName,
          colorPrimary: progressBarBackgroundClassName,
        }}
      />
    </div>
  );
});

export default PasswordStrengthBar;
